import React, { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import TranslatedText from 'components/atoms/translated_text';
import {
  AppDatePicker,
  AppSelect,
  AppTextField,
} from 'components/molecules/form_controls';
import GridLayout from 'components/molecules/grid_layout';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import VatRateActions from 'redux/reducers/vat_rate_reducer/actions';
import * as VatRateSelectors from 'redux/reducers/vat_rate_reducer/selectors';
import * as DateUtils from 'utils/dates';
import { Languages, Environment } from 'config';
import useStyles from './styles';
import { StateStatus } from 'redux/utils/common';
import request from 'utils/request';

const Translations = {
  country_control: {
    [Languages.Greek]: 'Country',
    [Languages.English]: 'Country',
  },
  vat_rate_type_control: {
    [Languages.Greek]: 'VAT Rate Type',
    [Languages.English]: 'VAT Rate Type',
  },
  vat_rate_control: {
    [Languages.Greek]: 'VAT Rate (%)',
    [Languages.English]: 'VAT Rate (%)',
  },
  valid_from_control: {
    [Languages.Greek]: 'Valid From',
    [Languages.English]: 'Valid From',
  },
  valid_to_control: {
    [Languages.Greek]: 'Valid To',
    [Languages.English]: 'Valid To',
  },
  submit: {
    [Languages.Greek]: 'Save',
    [Languages.English]: 'Save',
  },
};

interface FormFields {
  country_id: string;
  vat_rate_type_id: string;
  vat_rate: string;
  valid_from: Date | null;
  valid_to: Date | null;
}

export default function AddVatRateForm(): ReactElement {
  const dispatch = useDispatch();
  const classes = useStyles();
  const vatRateTypes = VatRateSelectors.useGetVatRateTypes();
  const [countries, setCountries] = React.useState<any[]>([]);

  React.useEffect(() => {
    request('get', `${Environment.API_BASE_URL}/api/country`).then(
      (res: any) => {
        if (res.data && res.data.data) {
          setCountries(res.data.data);
        }
      }
    );
  }, []);

  const countryOptions = countries.map((c: any) => ({
    id: String(c.country_id),
    label: `${c.code} - ${c.description}`,
  }));

  const typeOptions =
    vatRateTypes.status === StateStatus.Success && vatRateTypes.data
      ? vatRateTypes.data.map((t: any) => ({
          id: String(t.vat_rate_type_id),
          label: t.description,
        }))
      : [];

  const defaultValues: FormFields = {
    country_id: '',
    vat_rate_type_id: '',
    vat_rate: '',
    valid_from: null,
    valid_to: null,
  };

  const { errors, control, handleSubmit } = useForm<FormFields>({ defaultValues });

  const onSubmit = (data: FormFields) => {
    dispatch(
      VatRateActions.addVatRateAction({
        country_id: parseInt(data.country_id),
        vat_rate_type_id: parseInt(data.vat_rate_type_id),
        vat_rate: parseFloat(data.vat_rate),
        valid_from: DateUtils.DateInQueryFormat(data.valid_from as Date),
        valid_to: data.valid_to ? DateUtils.DateInQueryFormat(data.valid_to) : null,
      })
    );
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      <GridLayout
        elements={[
          {
            element: (
              <AppSelect
                variant="outlined"
                margin="normal"
                fullWidth
                required
                id="country_id"
                label={<TranslatedText textMap={Translations.country_control} />}
                error={'country_id' in errors}
                name="country_id"
                control={control}
                rules={{ required: 'Required Field' }}
                options={countryOptions}
                helperText={errors.country_id && errors.country_id.message}
              />
            ),
            size: 12,
            id: 'country_id',
          },
          {
            element: (
              <AppSelect
                variant="outlined"
                margin="normal"
                fullWidth
                required
                id="vat_rate_type_id"
                label={<TranslatedText textMap={Translations.vat_rate_type_control} />}
                error={'vat_rate_type_id' in errors}
                name="vat_rate_type_id"
                control={control}
                rules={{ required: 'Required Field' }}
                options={typeOptions}
                helperText={errors.vat_rate_type_id && errors.vat_rate_type_id.message}
              />
            ),
            size: 12,
            id: 'vat_rate_type_id',
          },
          {
            element: (
              <AppTextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="vat_rate"
                label={<TranslatedText textMap={Translations.vat_rate_control} />}
                error={'vat_rate' in errors}
                name="vat_rate"
                control={control}
                rules={{
                  required: 'Required Field',
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: 'Enter a valid number (e.g. 20 or 20.50)',
                  },
                }}
                helperText={errors.vat_rate && errors.vat_rate.message}
              />
            ),
            size: 12,
            id: 'vat_rate',
          },
          {
            element: (
              <AppDatePicker
                fullWidth
                margin="normal"
                label={<TranslatedText textMap={Translations.valid_from_control} />}
                error={'valid_from' in errors}
                control={control}
                required
                rules={{ required: 'Required Field' }}
                autoOk
                name="valid_from"
                helperText={errors.valid_from && errors.valid_from.message}
              />
            ),
            size: 12,
            id: 'valid_from',
          },
          {
            element: (
              <AppDatePicker
                fullWidth
                margin="normal"
                label={<TranslatedText textMap={Translations.valid_to_control} />}
                error={'valid_to' in errors}
                control={control}
                rules={{}}
                autoOk
                name="valid_to"
                helperText={errors.valid_to && errors.valid_to.message}
              />
            ),
            size: 12,
            id: 'valid_to',
          },
          {
            element: (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ width: '100%' }}
              >
                <TranslatedText textMap={Translations.submit} />
              </Button>
            ),
            size: 12,
            id: 'action',
          },
        ]}
      />
    </form>
  );
}
